import AgoraRTC, { type ICameraVideoTrack } from 'agora-rtc-react'
import { showAlert } from '../../../../../../_metronic/helpers'
import { getCSSVariableValue } from '../../../../../../_metronic/assets/ts/_utils'
import VirtualBackgroundExtension, {
  type IVirtualBackgroundProcessor,
} from 'agora-extension-virtual-background'
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { type BackgroundType } from '../types'

const CUSTOM_BACKGROUNDS_STORAGE_KEY = 'customBackgrounds'
const BACKGROUND_TYPE_STORAGE_KEY = 'agora-bg-type'
const BACKGROUND_URL_STORAGE_KEY = 'agora-bg-url'
const WASM_BASE_PATH = '/assets/wasms'
const ALLOWED_BACKGROUND_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_BACKGROUND_WIDTH = 1280
const MAX_BACKGROUND_HEIGHT = 720
const BACKGROUND_JPEG_QUALITY = 0.85

const BACKGROUND_TYPES: readonly BackgroundType[] = ['none', 'blur', 'image']

type UseVirtualBackgroundParams = {
  isPaciente: boolean
  localCameraTrack?: ICameraVideoTrack | null
}

const readStoredBackgroundType = (): BackgroundType => {
  try {
    const saved = localStorage.getItem(BACKGROUND_TYPE_STORAGE_KEY)
    return BACKGROUND_TYPES.includes(saved as BackgroundType) ? (saved as BackgroundType) : 'none'
  } catch {
    return 'none'
  }
}

const readStoredBackgroundUrl = (): string | null => {
  try {
    return localStorage.getItem(BACKGROUND_URL_STORAGE_KEY)
  } catch {
    return null
  }
}

const createDefaultBackgroundImage = async () => {
  const canvas = document.createElement('canvas')
  canvas.width = 1280
  canvas.height = 720

  const context = canvas.getContext('2d')
  if (context) {
    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height)
    // Canvas não resolve `var()`.
    gradient.addColorStop(0, getCSSVariableValue('--brand-primary-dark'))
    gradient.addColorStop(1, getCSSVariableValue('--brand-primary'))
    context.fillStyle = gradient
    context.fillRect(0, 0, canvas.width, canvas.height)
  }

  const image = new Image()
  image.src = canvas.toDataURL()

  await new Promise<void>((resolve) => {
    image.onload = () => resolve()
  })

  return image
}

const loadImage = async (imageUrl: string) => {
  const image = new Image()
  image.crossOrigin = 'anonymous'
  image.src = imageUrl

  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = () => reject(new Error('Falha ao carregar imagem'))
  })

  return image
}

const compressBackgroundImage = async (file: File): Promise<string> => {
  const objectUrl = URL.createObjectURL(file)

  try {
    const image = await loadImage(objectUrl)

    const scale = Math.min(
      1,
      MAX_BACKGROUND_WIDTH / image.naturalWidth,
      MAX_BACKGROUND_HEIGHT / image.naturalHeight
    )
    const width = Math.round(image.naturalWidth * scale)
    const height = Math.round(image.naturalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error('Canvas 2D indisponível para comprimir a imagem de fundo')
    }

    context.drawImage(image, 0, 0, width, height)

    return canvas.toDataURL('image/jpeg', BACKGROUND_JPEG_QUALITY)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export const useVirtualBackground = ({
  isPaciente,
  localCameraTrack,
}: UseVirtualBackgroundParams) => {
  const [processor, setProcessor] = useState<IVirtualBackgroundProcessor | null>(null)
  const [backgroundType, setBackgroundType] = useState<BackgroundType>(readStoredBackgroundType)
  const [showBackgroundMenu, setShowBackgroundMenu] = useState(false)
  const [showBackgroundModal, setShowBackgroundModal] = useState(false)
  const [previewBackground, setPreviewBackground] = useState<string | null>(null)
  const [customBackgrounds, setCustomBackgrounds] = useState<string[]>([])
  const [selectedBackgroundUrl, setSelectedBackgroundUrl] = useState<string | null>(
    readStoredBackgroundUrl
  )
  const processorRef = useRef<IVirtualBackgroundProcessor | null>(null)
  const extensionRef = useRef<VirtualBackgroundExtension | null>(null)
  const extensionRegisteredRef = useRef(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const applyRequestIdRef = useRef(0)

  useEffect(() => {
    if (isPaciente) return

    const savedBackgrounds = localStorage.getItem(CUSTOM_BACKGROUNDS_STORAGE_KEY)
    if (!savedBackgrounds) return

    try {
      const parsedBackgrounds = JSON.parse(savedBackgrounds)

      if (Array.isArray(parsedBackgrounds)) {
        setCustomBackgrounds(
          parsedBackgrounds.filter((background) => typeof background === 'string')
        )
      }
    } catch {
      localStorage.removeItem(CUSTOM_BACKGROUNDS_STORAGE_KEY)
    }
  }, [isPaciente])

  useEffect(() => {
    if (isPaciente) return

    try {
      localStorage.setItem(BACKGROUND_TYPE_STORAGE_KEY, backgroundType)
    } catch {
      //
    }
  }, [isPaciente, backgroundType])

  useEffect(() => {
    if (isPaciente) return

    try {
      if (selectedBackgroundUrl) {
        localStorage.setItem(BACKGROUND_URL_STORAGE_KEY, selectedBackgroundUrl)
      } else {
        localStorage.removeItem(BACKGROUND_URL_STORAGE_KEY)
      }
    } catch {
      //
    }
  }, [isPaciente, selectedBackgroundUrl])

  useEffect(() => {
    if (isPaciente || !localCameraTrack) return

    let cancelled = false

    const initVirtualBackground = async () => {
      if (!extensionRef.current) {
        extensionRef.current = new VirtualBackgroundExtension()
      }

      const extension = extensionRef.current

      if (!extension.checkCompatibility()) return

      if (!extensionRegisteredRef.current) {
        try {
          AgoraRTC.registerExtensions([extension])
        } catch {
          //
        }

        extensionRegisteredRef.current = true
      }

      if (processorRef.current) return

      const newProcessor = extension.createProcessor()
      if (!newProcessor) return

      try {
        await newProcessor.init(WASM_BASE_PATH)
      } catch {
        return
      }

      if (cancelled) {
        try {
          newProcessor.release()
        } catch {
          //
        }
        return
      }

      processorRef.current = newProcessor
      setProcessor(newProcessor)
    }

    initVirtualBackground()

    return () => {
      cancelled = true

      if (processorRef.current) {
        try {
          localCameraTrack.unpipe()
        } catch {
          //
        }
      }
    }
  }, [isPaciente, localCameraTrack])

  useEffect(() => {
    return () => {
      if (processorRef.current) {
        try {
          processorRef.current.release()
        } catch {
          //
        }

        processorRef.current = null
      }

      extensionRef.current = null
      extensionRegisteredRef.current = false
    }
  }, [])

  const persistCustomBackgrounds = useCallback((backgrounds: string[]) => {
    try {
      localStorage.setItem(CUSTOM_BACKGROUNDS_STORAGE_KEY, JSON.stringify(backgrounds))
      return true
    } catch {
      showAlert({
        title: 'Atenção',
        text: 'Não foi possível salvar o fundo. Remova alguma imagem e tente novamente.',
        icon: 'warning',
      })
      return false
    }
  }, [])

  const applyVirtualBackground = useCallback(
    async (type: BackgroundType, imageUrl?: string, interactive: boolean = true) => {
      setBackgroundType(type)
      setSelectedBackgroundUrl(imageUrl || null)
      setShowBackgroundMenu(false)
      setShowBackgroundModal(false)

      const activeProcessor = processorRef.current
      if (!activeProcessor || !localCameraTrack) return

      const requestId = ++applyRequestIdRef.current

      try {
        if (type === 'none') {
          try {
            localCameraTrack.unpipe()
          } catch {
            //
          }

          activeProcessor.disable()
          setBackgroundType('none')
          setSelectedBackgroundUrl(null)
        } else if (type === 'blur') {
          activeProcessor.setOptions({ type: 'blur', blurDegree: 2 })
          localCameraTrack.pipe(activeProcessor).pipe(localCameraTrack.processorDestination)
          activeProcessor.enable()
          setBackgroundType('blur')
          setSelectedBackgroundUrl(null)
        } else {
          const image = imageUrl ? await loadImage(imageUrl) : await createDefaultBackgroundImage()

          // Outra aplicação de fundo foi iniciada durante o carregamento da imagem
          if (requestId !== applyRequestIdRef.current) return

          activeProcessor.setOptions({ type: 'img', source: image, fit: 'cover' })
          localCameraTrack.pipe(activeProcessor).pipe(localCameraTrack.processorDestination)
          activeProcessor.enable()
        }
      } catch {
        if (interactive) {
          showAlert({
            title: 'Atenção',
            text: 'Erro ao aplicar fundo. Verifique se a imagem é válida.',
            icon: 'warning',
          })
        } else {
          setBackgroundType('none')
          setSelectedBackgroundUrl(null)
        }
      }
    },
    [localCameraTrack]
  )

  useEffect(() => {
    if (!processor || !localCameraTrack || backgroundType === 'none') return

    applyVirtualBackground(backgroundType, selectedBackgroundUrl || undefined, false)
  }, [applyVirtualBackground, backgroundType, localCameraTrack, processor, selectedBackgroundUrl])

  const handleFileUpload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      if (!ALLOWED_BACKGROUND_IMAGE_TYPES.includes(file.type)) {
        showAlert({
          title: 'Atenção',
          text: 'Por favor, selecione uma imagem JPG, PNG ou WebP.',
          icon: 'warning',
        })
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        showAlert({ title: 'Atenção', text: 'A imagem deve ter no máximo 5MB.', icon: 'warning' })
        return
      }

      let dataUrl: string
      try {
        dataUrl = await compressBackgroundImage(file)
      } catch {
        showAlert({
          title: 'Atenção',
          text: 'Não foi possível ler a imagem selecionada.',
          icon: 'warning',
        })
        return
      }

      const nextBackgrounds = [...customBackgrounds, dataUrl]
      const didPersist = persistCustomBackgrounds(nextBackgrounds)

      if (!didPersist) return

      setCustomBackgrounds(nextBackgrounds)
      setPreviewBackground(dataUrl)
    },
    [customBackgrounds, persistCustomBackgrounds]
  )

  const removeCustomBackground = useCallback(
    (index: number) => {
      const nextBackgrounds = customBackgrounds.filter((_, currentIndex) => currentIndex !== index)
      setCustomBackgrounds(nextBackgrounds)
      persistCustomBackgrounds(nextBackgrounds)
    },
    [customBackgrounds, persistCustomBackgrounds]
  )

  const applyPreview = useCallback(() => {
    if (!previewBackground) return

    applyVirtualBackground('image', previewBackground)
    setPreviewBackground(null)
  }, [applyVirtualBackground, previewBackground])

  const closeBackgroundModal = useCallback(() => {
    setShowBackgroundModal(false)
    setPreviewBackground(null)
  }, [])

  const openBackgroundModal = useCallback(() => {
    setShowBackgroundMenu(false)
    setShowBackgroundModal(true)
  }, [])

  return {
    processor,
    backgroundType,
    showBackgroundMenu,
    setShowBackgroundMenu,
    showBackgroundModal,
    previewBackground,
    setPreviewBackground,
    customBackgrounds,
    selectedBackgroundUrl,
    fileInputRef,
    applyVirtualBackground,
    handleFileUpload,
    removeCustomBackground,
    applyPreview,
    closeBackgroundModal,
    openBackgroundModal,
  }
}
